import { useEffect, useState } from 'react';
import type { SleepLocation } from '@shared/constants.ts';
import { SLEEP_LOCATIONS } from '@shared/constants.ts';
import type { SleepSession } from '@shared/types.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, FieldRow, SegRow } from './SheetShell.tsx';
import { fmtTime, fmtDur } from '../../lib/format.ts';
import { TimePicker } from './TimePicker.tsx';

export type SleepSheetMode = 'start' | 'end' | 'adjust';

type Props = {
  open: boolean;
  onClose: () => void;
  mode: SleepSheetMode;
  session?: SleepSession | null;
  defaultLocation?: SleepLocation;
  onSubmit: (vars: {
    location: SleepLocation;
    notes?: string;
    started_at?: number;
  }) => Promise<unknown> | void;
};

export function SleepSheet({
  open,
  onClose,
  mode,
  session,
  defaultLocation = 'cot',
  onSubmit,
}: Props) {
  const [location, setLocation] = useState<SleepLocation>(
    (session?.location as SleepLocation) ?? defaultLocation,
  );
  const [notes, setNotes] = useState(session?.notes ?? '');
  const [startedAt, setStartedAt] = useState<number | null>(session?.started_at ?? null);

  useEffect(() => {
    if (!open) return;
    if (session?.location) setLocation(session.location as SleepLocation);
    else setLocation(defaultLocation);
    setNotes(session?.notes ?? '');
    setStartedAt(session?.started_at ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session?.id, session?.location, session?.notes, session?.started_at]);

  const now = Math.floor(Date.now() / 1000);
  const durMin = startedAt ? Math.floor((now - startedAt) / 60) : 0;

  const title =
    mode === 'end' ? 'End sleep' : mode === 'adjust' ? 'Adjust sleep' : 'Start sleep';
  const primaryLabel =
    mode === 'end' ? 'End sleep' : mode === 'adjust' ? 'Save' : 'Start sleep';
  const tagline =
    mode === 'start'
      ? 'Where will Lion sleep?'
      : startedAt
        ? `In progress · started ${fmtTime(startedAt)} · ${fmtDur(durMin)}`
        : 'In progress';

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={title}
      tagline={tagline}
      accent="sleep-night"
      icon={(p) => <Icon.moon {...p} />}
      primaryLabel={primaryLabel}
      onPrimary={async () => {
        const changedStart =
          startedAt != null && session?.started_at != null && startedAt !== session.started_at;
        await onSubmit({
          location,
          notes: notes.trim() || undefined,
          started_at: changedStart ? startedAt! : undefined,
        });
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {mode === 'end' && startedAt && (
          <div style={{ display: 'flex', gap: 8 }}>
            <FieldRow label="Start" value={fmtTime(startedAt)} hint={fmtDur(durMin)} />
            <FieldRow label="End" value="now" hint={fmtTime(now)} />
          </div>
        )}
        {mode === 'adjust' && startedAt != null && (
          <TimePicker
            label="Started at"
            value={startedAt}
            onChange={setStartedAt}
            hint={`${fmtDur(Math.max(0, Math.floor((now - startedAt) / 60)))} so far`}
          />
        )}
        <SegRow
          label="Location"
          active={location}
          onChange={(v) => setLocation(v)}
          options={SLEEP_LOCATIONS.map((id) => ({ id, label: cap(id) }))}
        />
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.04,
              marginBottom: 6,
              paddingLeft: 4,
            }}
          >
            Notes
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="optional"
            rows={3}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 14,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-1)',
              fontSize: 14,
              resize: 'none',
            }}
          />
        </div>
      </div>
    </SheetShell>
  );
}

const cap = (s: string) => s[0]?.toUpperCase() + s.slice(1);
